import uid from "common/utils/uid";
import type { FilterEntitySourceId } from "../buildingGraphFilter/entitySource";
import type { PrimaryEntityId } from "../doc/primaryEntityId";
import type { DocEntityBase } from "../entities/docEntityBase";
import { docEntityBaseNew } from "../entities/docEntityBaseNew";
import type {
  FloorHandle,
  FreeformWallEdgeHandle,
  FreeformWallHandle,
  LevelHandle,
  RoofHandle,
  RoomEdgeHandle,
  RoomHandle,
} from "../handles/docEntityHandleTypes";

export type PredicateOp = "EQ" | "NEQ" | "IN" | "CONTAINS" | "INTERSECTS" | "EXISTS";

export interface DocBuildingGraphFilterGraph {
  readonly rootNodeId: string;
  readonly nodes: Record<string, DocBuildingGraphFilterNode>;
}

export type DocBuildingGraphFilterNode =
  | DocBuildingGraphAndFilterNode
  | DocBuildingGraphOrFilterNode
  | DocBuildingGraphNotFilterNode
  | DocBuildingGraphPredicateFilterNode;

export type DocBuildingGraphPredicateLiteral =
  | string
  | number
  | boolean
  | null
  | RoomHandle
  | LevelHandle
  | FloorHandle
  | RoofHandle
  | RoomEdgeHandle
  | FreeformWallHandle
  | FreeformWallEdgeHandle;

interface DocBuildingGraphFilterNodeBase<F extends string> {
  readonly filterType: F;
  readonly targetType: FilterEntitySourceId;
}

export interface DocBuildingGraphAndFilterNode extends DocBuildingGraphFilterNodeBase<"AND"> {
  readonly childrenNodeIds: readonly string[];
}

export interface DocBuildingGraphOrFilterNode extends DocBuildingGraphFilterNodeBase<"OR"> {
  readonly childrenNodeIds: readonly string[];
}

export interface DocBuildingGraphNotFilterNode extends DocBuildingGraphFilterNodeBase<"NOT"> {
  readonly childNodeId: string;
}

export interface DocBuildingGraphPredicateFilterNode extends DocBuildingGraphFilterNodeBase<"PREDICATE"> {
  readonly op: PredicateOp;
  readonly path: string;
  readonly values?: readonly DocBuildingGraphPredicateLiteral[];
  readonly valueSource?: DocBuildingGraphSubfilterValueSource;
}

export interface DocBuildingGraphSubfilterValueSource {
  readonly sourceType: FilterEntitySourceId;
  readonly selectPaths: readonly string[];
  readonly filterGraphId?: string;
}

/**
 * Rule-authored face side direction. Set on wall plane rules and preserved
 * unchanged across every sub-filter projection. The leaf
 * {@link BuildingGraphFaceNodeSideHandle} carries this value verbatim.
 *
 * `POSITIVE_NORMAL` = outward from the face node's volume (e.g. exterior side
 * of a room's wall). `NEGATIVE_NORMAL` = inward into the volume (interior side).
 */
export type DocFaceDirection = "POSITIVE_NORMAL" | "NEGATIVE_NORMAL";

export function oppositeDocFaceDirection(direction: DocFaceDirection): DocFaceDirection {
  return direction === "POSITIVE_NORMAL" ? "NEGATIVE_NORMAL" : "POSITIVE_NORMAL";
}

/**
 * Sub-filter mode applied to the matched FACE_NODE rows. Each mode produces a
 * different boundary handle type but always preserves rule direction via the leaf
 * face-node side handle.
 *
 * - `UNDEFINED`: pass-through. Result is the FACE_NODE_SIDE handles directly.
 * - `PERIMETER`: each matched FACE_NODE expands into TOPOLOGY_FACE_SIDE handles
 *   for topology faces whose parent face nodes include the match AND that have
 *   `isPerimeter === true`.
 * - `OPPOSITE`: each matched FACE_NODE expands into FACE_EDGE_SIDE handles for
 *   incident through-wall OVERLAP edges (`faceEdgeDot < 0`) whose other face node
 *   passes the OPPOSITE predicate sub-graph keyed by `oppositeFilterGraphId`.
 *   Same-direction OVERLAP and EDGE adjacency are excluded.
 */
export type DocFaceNodeSubFilter =
  | { readonly kind: "UNDEFINED" }
  | { readonly kind: "PERIMETER" }
  | { readonly kind: "OPPOSITE"; readonly oppositeFilterGraphId: string };

export interface DocWallPlaneFilter extends DocEntityBase<"WALL_PLANE_FILTER"> {
  readonly id: PrimaryEntityId<"WALL_PLANE_FILTER">;
  /** Root predicate graph (targets FACE_NODE rows). */
  readonly filterGraphId: string;
  /** All graphs in this filter — root + any OPPOSITE predicate graph + any internal `valueSource` graphs. */
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
  readonly subFilter: DocFaceNodeSubFilter;
}

export function makeEmptyDocWallPlaneFilterGraph(targetType: FilterEntitySourceId = "FACE_NODE"): {
  readonly filterGraphId: string;
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
} {
  const filterGraphId = `graph_${uid()}`;
  const rootNodeId = `node_${uid()}`;
  return {
    filterGraphId,
    filterGraphs: {
      [filterGraphId]: {
        rootNodeId,
        nodes: {
          [rootNodeId]: {
            filterType: "AND",
            targetType,
            childrenNodeIds: [],
          },
        },
      },
    },
  };
}

/** Alias for callers that refer to filter graphs without the wall-plane prefix. */
export const makeEmptyDocBuildingGraphFilterGraph = makeEmptyDocWallPlaneFilterGraph;

export function wallPlaneFilterNew({
  name = "Filter",
  id = uid(),
  sortIndex = 0,
  filterGraphId,
  filterGraphs,
  subFilter,
}: Partial<DocWallPlaneFilter> = {}): DocWallPlaneFilter {
  const emptyGraph = makeEmptyDocWallPlaneFilterGraph("FACE_NODE");
  const resolvedFilterGraphId = filterGraphId ?? emptyGraph.filterGraphId;
  const resolvedFilterGraphs =
    filterGraphs ?? seedCatalogFilterGraphIsFreeformFalse(emptyGraph.filterGraphs, emptyGraph.filterGraphId);
  return {
    ...docEntityBaseNew("WALL_PLANE_FILTER", id),
    name,
    sortIndex,
    filterGraphId: resolvedFilterGraphId,
    filterGraphs: resolvedFilterGraphs,
    subFilter: subFilter ?? { kind: "UNDEFINED" },
  };
}

/** New catalog filters default to excluding freeform wall face nodes. */
function seedCatalogFilterGraphIsFreeformFalse(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  filterGraphId: string,
): Record<string, DocBuildingGraphFilterGraph> {
  const graph = filterGraphs[filterGraphId];
  if (!graph) {
    return filterGraphs;
  }
  const rootNode = graph.nodes[graph.rootNodeId];
  if (!rootNode || rootNode.filterType !== "AND" || rootNode.targetType !== "FACE_NODE") {
    return filterGraphs;
  }
  const predicateNodeId = `node_${uid()}`;
  return {
    ...filterGraphs,
    [filterGraphId]: {
      ...graph,
      nodes: {
        ...graph.nodes,
        [predicateNodeId]: {
          filterType: "PREDICATE",
          targetType: "FACE_NODE",
          op: "EQ",
          path: "isFreeform",
          values: [false],
        },
        [graph.rootNodeId]: {
          ...rootNode,
          childrenNodeIds: [...rootNode.childrenNodeIds, predicateNodeId],
        },
      },
    },
  };
}
